import { Fragment, useMemo } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { useNavigate, useParams } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetEntitlementGrantQuery,
  useUpdateEntitlementGrantMutation,
} from '@/services/EntitlementGrantService';
import { EntitlementGrantForm } from '@/pages/entitlement-grants/components/EntitlementGrantForm';
import {
  EntitlementGrantStatus,
  EntitlementGrantType,
} from '@/types/entitlement-grant.types';
import { useSnackbar } from 'notistack';

// Convert HTML string to plain text by stripping tags and decoding entities
const toPlainText = (html: unknown): string => {
  if (typeof html !== 'string') return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent || div.innerText || '').trim();
};

const EntitlementGrantConfiguration = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = usePermissions();
  const canRead = hasPermission('entitlement_grant_management_read');
  const canCreate = hasPermission('entitlement_grant_management_create');
  const canUpdate = hasPermission('entitlement_grant_management_update');

  const isCreateMode = !id;

  const { data: grantResponse, isLoading } = useGetEntitlementGrantQuery(id!, {
    skip: !id || !canRead,
  });
  const [updateGrant, { isLoading: isCancelling }] =
    useUpdateEntitlementGrantMutation();

  const grant = grantResponse?.data;

  const readOnlyReason = useMemo(() => {
    if (!canRead)
      return "You don't have permission to view entitlement grants.";
    if (isCreateMode && !canCreate) {
      return "You don't have permission to create entitlement grants.";
    }
    if (isLoading) return 'Loading entitlement grant...';
    return undefined;
  }, [canCreate, canRead, isCreateMode, isLoading]);

  const handleCancelGrant = async () => {
    if (!grant?.id) return;
    try {
      await updateGrant({
        id: grant.id,
        payload: { status: EntitlementGrantStatus.CANCELLED },
      }).unwrap();
      enqueueSnackbar('Entitlement grant cancelled', { variant: 'success' });
      navigate('/configuration/entitlement-grants');
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ||
        'Failed to cancel entitlement grant';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  if (readOnlyReason) {
    return (
      <Container>
        <p>{readOnlyReason}</p>
      </Container>
    );
  }

  if (isCreateMode) {
    return (
      <Fragment>
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle
                  id="entitlement-grant-create-heading"
                  text="Create Entitlement Grant"
                />
                <ToolbarDescription>
                  Create a manual capability elevation grant.
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
          </Container>
        )}
        <Container>
          <EntitlementGrantForm />
        </Container>
      </Fragment>
    );
  }

  if (!grant) {
    return (
      <Container>
        <p>Entitlement grant not found.</p>
      </Container>
    );
  }

  const canCancel =
    canUpdate &&
    grant.grant_type === EntitlementGrantType.MANUAL &&
    grant.status === EntitlementGrantStatus.ACTIVE;

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="entitlement-grant-detail-heading" />
              <ToolbarDescription>
                View entitlement grant details and lifecycle.
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              <button
                type="button"
                className="btn btn-sm btn-light"
                onClick={() => navigate('/configuration/entitlement-grants')}
              >
                Back to list
              </button>
              {canCancel && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={isCancelling}
                  onClick={() => void handleCancelGrant()}
                >
                  Cancel Grant
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <Container>
        <div className="grid gap-3">
          <div>
            <strong>ID:</strong> {grant.id}
          </div>
          <div>
            <strong>Actor:</strong> {grant.actor_type} /{' '}
            {grant.actor_name || grant.actor_id}
          </div>
          <div>
            <strong>Grant Type:</strong> {grant.grant_type}
          </div>
          <div>
            <strong>Status:</strong> {grant.status}
          </div>
          <div>
            <strong>Window:</strong>{' '}
            {new Date(grant.start_at).toLocaleString()} -{' '}
            {new Date(grant.end_at).toLocaleString()}
          </div>
          <div>
            <strong>Source:</strong> {grant.source || '-'}
          </div>
          <div>
            <strong>Subscription:</strong>{' '}
            {toPlainText(grant.subscription_name) ||
              grant.reference_subscription_id ||
              '-'}
          </div>
          <div>
            <strong>Created By:</strong>{' '}
            {grant.created_by_name || grant.created_by || '-'}
          </div>
          {grant.cancelled_at && (
            <div>
              <strong>Cancelled At:</strong>{' '}
              {new Date(grant.cancelled_at).toLocaleString()}
            </div>
          )}
        </div>
      </Container>
    </Fragment>
  );
};

export { EntitlementGrantConfiguration };
